import { toast as sonnerToast, type ExternalToast } from 'sonner';
import {
    decorateToastsInDom,
    registerToastTracking,
    type ToastTrackingOptions,
} from '@/lib/toast/toastTracking';

/**
 * Wrapper around sonner adding per-type durations (4s info/success, 6s
 * warning/error) and dedupe of identical back-to-back messages. Styling and
 * positioning live on the <Toaster> in components/ui/sonner.tsx.
 */

type ToastMessage = Parameters<typeof sonnerToast>[0];
type ToastType = 'success' | 'error' | 'warning' | 'info';
export type TrackedToastOptions = ExternalToast & ToastTrackingOptions;

const DEFAULT_DURATION = 4000;

const DURATIONS: Record<ToastType, number> = {
    success: 4000,
    info: 4000,
    warning: 6000,
    error: 6000,
};

// Identical content maps to the same id; sonner replaces a toast sharing an id.
const hashId = (seed: string): string => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = (hash << 5) - hash + seed.charCodeAt(i);
        hash |= 0;
    }
    return `toast-${hash}`;
};

const withDefaults = (variant: ToastType | 'default', message: ToastMessage, data?: TrackedToastOptions): ExternalToast => {
    const {
        dataTrackActionClick,
        dataTrackActionButtonName,
        dataTrackCloseClick,
        dataTrackCloseButtonName,
        dataTrackLocation,
        ...sonnerOptions
    } = data ?? {};

    const duration = variant === 'default' ? DEFAULT_DURATION : DURATIONS[variant];
    const options: ExternalToast = { duration, ...sonnerOptions };

    // Only dedupe plain string messages; React nodes can't be keyed on.
    if (typeof message === 'string' && options.id === undefined) {
        options.id = hashId(`${variant}:${message}`);
    }

    if (dataTrackCloseClick || dataTrackActionClick) {
        registerToastTracking(options.id, {
            dataTrackActionClick,
            dataTrackActionButtonName,
            dataTrackCloseClick,
            dataTrackCloseButtonName,
            dataTrackLocation,
        });
    }

    return options;
};

const showToast = (variant: ToastType | 'default', message: ToastMessage, data?: TrackedToastOptions) => {
    const options = withDefaults(variant, message, data);
    const result = variant === 'default'
        ? sonnerToast(message, options)
        : sonnerToast[variant](message, options);

    queueMicrotask(decorateToastsInDom);

    return result;
};

const makeVariant = (variant: ToastType) => (message: ToastMessage, data?: TrackedToastOptions) =>
    showToast(variant, message, data);

const base = (message: ToastMessage, data?: TrackedToastOptions) =>
    showToast('default', message, data);

export const toast = Object.assign(base, {
    success: makeVariant('success'),
    error: makeVariant('error'),
    warning: makeVariant('warning'),
    info: makeVariant('info'),
    message: sonnerToast.message,
    loading: sonnerToast.loading,
    promise: sonnerToast.promise,
    custom: sonnerToast.custom,
    dismiss: sonnerToast.dismiss,
});
